import {
  Typography,
  Card,
  CardMedia,
  Grid,
  Box,
  Chip,
  Divider,
  Button,
} from "src/components";
import { useNavigate } from "react-router-dom";
import placeholderImage from "src/assets/placeholders/140x140.png"; 
import { getCategoryById } from 'src/data';

const CARD_HEIGHT = 300;
const IMAGE_HEIGHT = 120;
const CARD_BORDER_RADIUS = 2;

function capitalizeFirstLetter(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getArticleDescription(content) {
  if (!content) return "No description available";
  const plainText = content.replace(/[#*`]/g, "").substring(0, 100);
  return plainText.length < content.length ? plainText + "..." : plainText;
}

export default function SubCategories({ articles, category, categoryColor }) {
  const navigate = useNavigate();

  if (!articles?.length) return null;

  const handleArticleClick = (article) => {
    if (!category) {
      category = getCategoryById(article.categoryId);
      navigate(`/handbook/${category.slug}/${article.slug}`);
      return;
    }
    navigate(`/handbook/${category}/${article.slug}`)
  }

  return (
    <Grid container columnSpacing={2} rowSpacing={5} columns={{ xs: 4, sm: 8 }}>
      {articles.map((article) => (
        <Grid
          size={2}
          key={article.slug}
          sx={{ display: "flex" }}
        >
          <Card
            onClick={() => handleArticleClick(article)}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              height: CARD_HEIGHT,
              width: "100%",
              backgroundColor: "#FFFFFF",
              borderRadius: CARD_BORDER_RADIUS,
              boxShadow: 3,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                boxShadow: 6,
                transform: { md: "translateY(-4px)", xs: "none" },
              },
            }}
          >
            <CardMedia
              component="img"
              image={article.imageUrl || placeholderImage}
              alt={article.title}
              loading="lazy"
              sx={{
                height: IMAGE_HEIGHT,
                width: "calc(100% - 16px)", 
                margin: "8px auto 0",       
                objectFit: "cover",       
                borderRadius: 2,          
                backgroundColor: "#eee",
              }}
            />

            <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                noWrap
                title={article.title}
                sx={{ mb: 0.5 }}
              >
                {article.title}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  flexGrow: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {getArticleDescription(article.description)}
              </Typography>

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                {article.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={capitalizeFirstLetter(tag)}
                    size="small"
                    variant="filled"
                    sx={{backgroundColor: categoryColor, color: "#fff"}}
                  />
                ))}
              </Box>

              <Box sx={{ mt: "auto" }}>
                <Button
                  variant="text"
                  size="small"
                  disableRipple
                  sx={{
                    fontWeight: "bold",
                    textTransform: "none",
                    p: 0,
                    minWidth: 0,
                    "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                  }}
                >
                  Read more →
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
